/**
 * The studio's authentication fees as the Transaction Centre reads them — OND-70.
 *
 * Pure. OND-60's cases go in and a view comes out: only the cases the reader
 * is the STUDIO on, only those a fee has been raised on, each as one ledger
 * line, with the per-currency totals the strip draws.
 *
 * NOTHING HERE DECIDES ANYTHING THE DATABASE DECIDES. The fee's standing is
 * computed by `ondo.attribution_service_cases()` from the fee and its newest
 * correction; this file reads it and adds. The fee line itself is the
 * Verifications tab's `toCaseCard().fee`, reused rather than re-derived, so
 * the two screens cannot disagree about what one fee says.
 */

import type { AttributionServiceCase } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import type { Tile } from "../lib/Tiles.tsx";
import { money } from "../lib/money.ts";
import { toCaseCard } from "../verifications/attributionService.model.ts";
import type { FeeLine } from "../verifications/attributionService.model.ts";
import { ATTRIBUTION_FEES_COPY } from "./estate.constants.ts";

/** One fee, as the centre lists it. */
export interface FeeRow {
  requestId: string;
  title: string;
  /** "Erwin Wurm" — what the claim said. Null where the entry carried no name. */
  claimedArtistName: string | null;
  /** 'Asked by Dr Kohl · ONDO-4471'. */
  askedBy: string;
  /** 'Confirmed' or 'Rejected' — both are charged. */
  answered: string;
  fee: FeeLine;
}

export type AttributionFeesView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | { kind: "ready"; rows: FeeRow[]; totals: Tile[]; summary: string };

/** What one currency's fees add up to, in minor units. */
export interface FeeCurrencyTotal {
  currency: string;
  /** Standing `due`. */
  owedMinor: number;
  /** Standing `recorded_paid`. */
  paidMinor: number;
  /** Every fee in this currency that is not void. */
  rows: number;
  voided: number;
  refundsOwed: number;
}

export function toAttributionFeesView(input: {
  cases: AttributionServiceCase[] | null;
  error: string | null;
}): AttributionFeesView {
  const { cases, error } = input;

  if (error) return { kind: "error", title: ATTRIBUTION_FEES_COPY.errorTitle, message: error };
  if (!cases) return { kind: "loading", message: ATTRIBUTION_FEES_COPY.loading };

  const charged = chargedCases(cases);
  if (charged.length === 0) {
    return {
      kind: "empty",
      message: ATTRIBUTION_FEES_COPY.empty,
      detail: ATTRIBUTION_FEES_COPY.emptyDetail,
    };
  }

  return {
    kind: "ready",
    rows: charged.map(toFeeRow),
    totals: describeFeeTotals(cases),
    summary: ATTRIBUTION_FEES_COPY.ledger(charged.length),
  };
}

/**
 * THE STUDIO'S SIDE ONLY, with a fee on it.
 *
 * `ondo.attribution_service_cases()` returns both sides of every case the
 * caller is party to; a studio that has also ASKED for a confirmation of a work
 * it owns would otherwise see its own commission in its own fee ledger.
 */
export function chargedCases(cases: AttributionServiceCase[]): AttributionServiceCase[] {
  return cases.filter((row) => row.iAmTheStudio && row.fee !== null);
}

function toFeeRow(row: AttributionServiceCase): FeeRow {
  const card = toCaseCard(row);
  // Non-null by `chargedCases`; the card maps it one-to-one.
  const fee = card.fee as FeeLine;

  return {
    requestId: row.requestId,
    title: card.title,
    claimedArtistName: card.claimedArtistName,
    askedBy: `${ATTRIBUTION_FEES_COPY.askedBy} ${card.counterparty}`,
    answered: fee.rejected
      ? ATTRIBUTION_FEES_COPY.answered.rejected
      : ATTRIBUTION_FEES_COPY.answered.confirmed,
    fee,
  };
}

/**
 * Owed and recorded-as-paid, one pair of tiles per currency — the same shape
 * the resale settlements draw above their list, for the same reason: a fee is
 * denominated in its own currency and two currencies do not add.
 *
 * A VOID fee is counted in the note and in nothing else: it was owed and then
 * was not, and the row survives as a fact about the case rather than a figure
 * in this ledger. A refund owed is money going the OTHER way and is a count,
 * not a negative — a strip that netted it off would hide that a refund is
 * outstanding behind a smaller "owed" figure.
 */
export function describeFeeTotals(cases: AttributionServiceCase[]): Tile[] {
  return feeTotalsByCurrency(cases).flatMap((total) => {
    const parts = [ATTRIBUTION_FEES_COPY.totals.rows(total.rows)];
    if (total.voided > 0) parts.push(ATTRIBUTION_FEES_COPY.totals.voided(total.voided));
    if (total.refundsOwed > 0) {
      parts.push(ATTRIBUTION_FEES_COPY.totals.refunds(total.refundsOwed));
    }

    return [
      {
        key: `owed-${total.currency}`,
        label: ATTRIBUTION_FEES_COPY.totals.owed(total.currency),
        value: money(total.owedMinor, total.currency, "—"),
        note: parts.join(" · "),
      },
      {
        key: `paid-${total.currency}`,
        label: ATTRIBUTION_FEES_COPY.totals.paid(total.currency),
        value: money(total.paidMinor, total.currency, "—"),
        note: null,
      },
    ];
  });
}

/** The arithmetic behind `describeFeeTotals`, kept separate so a test can read numbers. */
export function feeTotalsByCurrency(cases: AttributionServiceCase[]): FeeCurrencyTotal[] {
  const byCurrency = new Map<string, FeeCurrencyTotal>();

  for (const row of chargedCases(cases)) {
    const fee = row.fee;
    if (!fee) continue;

    const total = byCurrency.get(fee.currency) ?? {
      currency: fee.currency,
      owedMinor: 0,
      paidMinor: 0,
      rows: 0,
      voided: 0,
      refundsOwed: 0,
    };

    switch (fee.standing) {
      case "void":
        total.voided += 1;
        break;
      case "due":
        total.rows += 1;
        total.owedMinor += fee.amountMinor;
        break;
      case "recorded_paid":
        total.rows += 1;
        total.paidMinor += fee.amountMinor;
        break;
      case "refund_due":
        total.rows += 1;
        total.refundsOwed += 1;
        break;
      case "refunded":
        total.rows += 1;
        break;
    }

    byCurrency.set(fee.currency, total);
  }

  return [...byCurrency.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/** What went wrong, in words a studio member can act on. */
export function describeAttributionFeesError(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;

  return cause instanceof Error ? cause.message : ATTRIBUTION_FEES_COPY.errorTitle;
}
